import { useMemo, useState } from "react";

import { Trash } from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DataRow,
  DataTable,
  Input,
  LoadingOverlay,
} from "@breakingthelines/design-system";

import {
  useAddTrustedSite,
  useRemoveTrustedSite,
  useTrustedSites,
} from "@/hooks/use-trusted-sites";
import type { TrustedSite } from "@/lib/admin-api";

import styles from "./trusted-sites.module.css";

const TABLE_COLUMNS =
  "minmax(190px, 1.4fr) minmax(90px, 0.6fr) minmax(200px, 1.3fr) minmax(110px, 0.7fr) minmax(90px, 0.6fr)";

function formatDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function TrustedSitesPage() {
  const { sites, loading, failed, error } = useTrustedSites();
  const addMutation = useAddTrustedSite();
  const removeMutation = useRemoveTrustedSite();

  const [domain, setDomain] = useState("");
  const [note, setNote] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [removeTarget, setRemoveTarget] = useState<TrustedSite | null>(null);

  const visibleSites = useMemo(() => {
    const sorted = [...sites].sort(
      (a, b) => (b.linkingArticleCount ?? 0) - (a.linkingArticleCount ?? 0),
    );
    const query = filterQuery.trim().toLowerCase();
    if (!query) return sorted;
    return sorted.filter(
      (site) =>
        site.domain.toLowerCase().includes(query) ||
        (site.note ?? "").toLowerCase().includes(query),
    );
  }, [sites, filterQuery]);

  const canSubmit = domain.trim().length > 0 && !addMutation.isPending;

  function handleAdd(event: React.FormEvent) {
    event.preventDefault();
    const trimmedDomain = domain.trim();
    if (!trimmedDomain) return;
    addMutation.mutate(
      { domain: trimmedDomain, note: note.trim() || undefined },
      {
        onSuccess: () => {
          setDomain("");
          setNote("");
        },
      },
    );
  }

  function handleConfirmRemove() {
    if (!removeTarget) return;
    removeMutation.mutate(
      { domain: removeTarget.domain },
      {
        onSuccess: () => setRemoveTarget(null),
      },
    );
  }

  return (
    <div className={styles.page}>
      <div>
        <h1 className={styles.title}>Trusted sites</h1>
        <p className={styles.subtitle}>
          Links from BTL articles to these sites count as editorial. All other links are marked
          sponsored. Subdomains are covered automatically.
        </p>
      </div>

      <form className={styles.addForm} onSubmit={handleAdd}>
        <Input
          aria-label="Domain"
          placeholder="bbc.co.uk"
          value={domain}
          onChange={(event) => setDomain(event.currentTarget.value)}
          className={styles.domainInput}
        />
        <Input
          aria-label="Note"
          placeholder="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
          className={styles.noteInput}
        />
        <Button type="submit" disabled={!canSubmit}>
          {addMutation.isPending ? "Adding…" : "Add site"}
        </Button>
      </form>
      {addMutation.isError ? (
        <p className={styles.actionError} role="alert">
          {errorMessage(addMutation.error, "Failed to add site.")}
        </p>
      ) : null}

      <div className={styles.filterRow}>
        <Input
          aria-label="Filter trusted sites"
          placeholder="Filter by domain or note"
          value={filterQuery}
          onChange={(event) => setFilterQuery(event.currentTarget.value)}
          className={styles.filterInput}
        />
      </div>

      <section className={styles.tableSection}>
        {loading ? <LoadingOverlay label="Loading trusted sites..." /> : null}
        <DataTable
          columns={TABLE_COLUMNS}
          header={["Domain", "Articles", "Note", "Added", "Actions"]}
          headerClassName={styles.tableHeader}
        >
          {visibleSites.map((site) => (
            <DataRow key={site.domain} className={styles.tableRow}>
              <span className={styles.mono} data-label="Domain">
                {site.domain}
              </span>
              <span className={styles.meta} data-label="Articles">
                {(site.linkingArticleCount ?? 0).toLocaleString()}
              </span>
              <span className={styles.meta} data-label="Note">
                {site.note}
              </span>
              <span className={styles.meta} data-label="Added">
                {formatDate(site.createdAt)}
              </span>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.removeButton}
                  disabled={removeMutation.isPending}
                  onClick={() => setRemoveTarget(site)}
                  aria-label={`Remove ${site.domain}`}
                >
                  <Trash size={14} />
                  Remove
                </button>
              </div>
            </DataRow>
          ))}
        </DataTable>

        {!loading && failed ? (
          <div className={styles.emptyState}>
            {errorMessage(error, "Failed to load trusted sites.")}
          </div>
        ) : null}
        {!loading && !failed && sites.length === 0 ? (
          <div className={styles.emptyState}>No trusted sites yet.</div>
        ) : null}
        {!loading && !failed && sites.length > 0 && visibleSites.length === 0 ? (
          <div className={styles.emptyState}>No trusted sites match that search.</div>
        ) : null}
      </section>

      <AlertDialog
        open={removeTarget != null}
        onOpenChange={(open) => {
          if (open) return;
          setRemoveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this trusted site?</AlertDialogTitle>
            <AlertDialogDescription>
              Links to {removeTarget?.domain} from BTL articles will be marked sponsored again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {removeMutation.isError ? (
            <p className={styles.actionError} role="alert">
              {errorMessage(removeMutation.error, "Failed to remove site.")}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleConfirmRemove}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
